import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Loader,
  ModalComponentWrapper,
  FormFieldSelectInput,
  LanguageInfo,
} from '@/shared/components';
import { useCountriesDictionary } from '@/shared/api';
import {
  useGenerateIncomeStatementForm,
  type GenerateIncomeStatementFormValues,
} from '../model';
import { useGenerateIncomeStatement } from '../api';
import { buildLanguageSelectOptions, useAppSettingsStore } from '@/shared/lib';
import type { LanguageSelectOption } from '@/shared/types';
import { useState } from 'react';

interface Props {
  onBack: () => void;
}

const GenerateIncomeStatementModal = ({ onBack }: Props) => {
  const { t } = useTranslation();

  const { countriesData, isCountriesLoading, countriesError } =
    useCountriesDictionary();

  const [documentLink, setDocumentLink] = useState<string>('');

  const { getLanguage } = useAppSettingsStore();

  const { form } = useGenerateIncomeStatementForm({
    language: getLanguage(),
  });

  const { generateIncomeStatement, isPending } = useGenerateIncomeStatement();

  if (isCountriesLoading || !countriesData) {
    return <Loader />;
  }

  if (countriesError) {
    return <div>Error</div>;
  }

  const clearDocumentLink = () => {
    if (documentLink) setDocumentLink('');
  };

  const onSubmit = async (data: GenerateIncomeStatementFormValues) => {
    if (documentLink) {
      window.open(documentLink, '_blank');
      return;
    }

    try {
      const response = await generateIncomeStatement(data.lang);
      setDocumentLink(response?.link || '');
    } catch (error) {
      clearDocumentLink();
      console.error(error);
    }
  };

  const languageOptions = buildLanguageSelectOptions(countriesData.value);

  const submitKey = documentLink ? 'open' : 'generate';

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.myDocuments.incomeStatement.title')}
      onBack={onBack}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col gap-[3.2rem]">
            <FormFieldSelectInput<LanguageSelectOption>
              name="lang"
              label={t('common.language.placeholder')}
              items={languageOptions}
              onValueChange={clearDocumentLink}
              renderItem={item => <LanguageInfo language={item} />}
              renderSelectedValue={value => (
                <LanguageInfo
                  language={languageOptions.find(item => item.value === value)}
                  className="gap-[0.8rem]"
                  iconClassName="w-[2.4rem] h-[2.4rem]"
                />
              )}
            />

            <Button
              size="lg"
              variant={documentLink ? 'default' : 'secondary'}
              type="submit"
              loading={isPending}>
              {t(
                `personalAccount.userProfile.myDocuments.incomeStatement.submitButton.${submitKey}`
              )}
            </Button>
          </div>
        </form>
      </Form>
    </ModalComponentWrapper>
  );
};

export { GenerateIncomeStatementModal };
